/**
 * Protected Route Component
 *
 * Purpose: Wraps components that require authentication and, optionally, an area (module)
 *          or permissions (ORCH-334, ORCH-21 §10.4)
 *
 * Architecture: Checks authentication state, then the user's modules and permissions in the
 *              active tenant, before rendering protected content. Redirects unauthenticated
 *              users to login and shows an access-denied screen to signed-in users who lack
 *              access. There is no role bypass: the server's module and permission lists decide.
 *
 * Dependencies: React, React Router, Material-UI, AuthContext
 * Author: Development Team
 * Created: 2025-01-27
 * Last Updated: 2025-08-06
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useAuth } from '../context/AuthContext';
import { ModuleKey, PermissionKey } from '../constants/permissionKeys';
import { ModuleLevel } from '../types/auth';

const GLOBAL_TENANT_ID = 'GLOBAL';

/**
 * Props interface for ProtectedRoute component
 */
interface ProtectedRouteProps {
  /** Child components to render if user is authenticated and authorized */
  children: React.ReactNode;
  /** Area the route belongs to; the user must hold it (and its parent) at `moduleLevel` */
  requiredModule?: ModuleKey;
  /** Level `requiredModule` must be held at. Default: read */
  moduleLevel?: ModuleLevel;
  /** The user must hold at least one of these permissions */
  requiredPermissions?: PermissionKey[];
  /** The active tenant must be GLOBAL (Tenant Manager, Component Library) */
  globalOnly?: boolean;
}

/**
 * Loading component for authentication check
 *
 * Purpose: Displays a loading indicator while checking authentication status
 * Logic: Renders a centered spinner with loading text
 * Input: None
 * Output: JSX element with loading indicator
 *
 * @returns JSX element for loading state
 */
const LoadingComponent: React.FC = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    gap={2}
  >
    <CircularProgress size={40} />
    <Typography variant="body1" color="textSecondary">
      Loading...
    </Typography>
  </Box>
);

/**
 * Access denied component
 *
 * Purpose: Tells a signed-in user they can't open this area. It never lists their roles
 *          or permissions.
 */
const AccessDeniedComponent: React.FC = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    gap={2}
    textAlign="center"
    p={4}
    data-testid="access-denied"
  >
    <Typography variant="h4" color="error" gutterBottom>
      Access Denied
    </Typography>
    <Typography variant="body1" color="textSecondary">
      You don&apos;t have access to this area. Ask an administrator if you need it.
    </Typography>
  </Box>
);

/**
 * Protected Route component
 *
 * Purpose: Renders its children only for a signed-in user who holds the route's area,
 *          permissions and tenant context
 * Logic: Loading → spinner (never a denial); signed out → login; otherwise every given gate
 *        must pass: `requiredModule` at `moduleLevel` with its parent, any of
 *        `requiredPermissions`, and the GLOBAL tenant when `globalOnly`.
 * Output: The protected content, the loading state, a redirect or the access-denied screen
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredModule,
  moduleLevel = 'read',
  requiredPermissions = [],
  globalOnly = false,
}) => {
  const { state, canAny, hasModule } = useAuth();
  const location = useLocation();

  if (state.loading) {
    return <LoadingComponent />;
  }

  if (!state.isAuthenticated || !state.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const allowed =
    (!requiredModule || hasModule(requiredModule, moduleLevel)) &&
    (requiredPermissions.length === 0 || canAny(requiredPermissions)) &&
    (!globalOnly || state.user.activeTenant?.tenant_id === GLOBAL_TENANT_ID);

  if (!allowed) {
    return <AccessDeniedComponent />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
